// Print Area Selection Tab
// Sprint 3: Area Selection UI

import React from 'react';
import { Label } from '@/app/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/app/components/ui/radio-group';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { FileSpreadsheet, FileCheck, Columns3, Rows3, MousePointerClick } from 'lucide-react';
import type { PrintArea, PrintAreaType } from '@/app/types/print';
import type { TableConfig } from '@/app/types/table';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PrintAreaTabProps {
  area: PrintArea;
  tableConfig: TableConfig;
  onUpdateArea: (updates: Partial<PrintArea>) => void;
}

export function PrintAreaTab({ area, tableConfig, onUpdateArea }: PrintAreaTabProps) {
  const { t } = useLanguage();

  const handleTypeChange = (type: PrintAreaType) => {
    onUpdateArea({ type });
  };
  
  const areaOptions: Array<{
    value: PrintAreaType;
    label: string;
    description: string;
    icon: React.ReactNode;
  }> = [
    {
      value: 'current-table',
      label: t.table.printDialog.area.options.entireTable.title,
      description: t.table.printDialog.area.options.entireTable.desc,
      icon: <FileSpreadsheet className="w-5 h-5" />,
    },
    {
      value: 'selected-range',
      label: t.table.printDialog.area.options.selectedRange.title,
      description: t.table.printDialog.area.options.selectedRange.desc,
      icon: <FileCheck className="w-5 h-5" />,
    },
    {
      value: 'selected-columns',
      label: t.table.printDialog.area.options.selectedColumns.title,
      description: t.table.printDialog.area.options.selectedColumns.desc,
      icon: <Columns3 className="w-5 h-5" />,
    },
    {
      value: 'selected-rows',
      label: t.table.printDialog.area.options.selectedRows.title,
      description: t.table.printDialog.area.options.selectedRows.desc,
      icon: <Rows3 className="w-5 h-5" />,
    },
    {
      value: 'custom-selection',
      label: t.table.printDialog.area.options.custom.title,
      description: t.table.printDialog.area.options.custom.desc,
      icon: <MousePointerClick className="w-5 h-5" />,
    },
  ];
  
  const showRangeInputs = area.type === 'selected-range' || area.type === 'custom-selection';
  const showColumnSelector = area.type === 'selected-columns';
  const showRowSelector = area.type === 'selected-rows';
  
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h3 className="text-lg font-medium mb-2">{t.table.printDialog.area.title}</h3>
        <p className="text-sm text-muted-foreground">
          {t.table.printDialog.area.subtitle}
        </p>
      </div>
      
      <RadioGroup value={area.type} onValueChange={handleTypeChange}>
        <div className="grid gap-3">
          {areaOptions.map((option) => (
            <Card
              key={option.value}
              className={`cursor-pointer transition-colors ${
                area.type === option.value
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-primary/50'
              }`}
              onClick={() => handleTypeChange(option.value)}
            >
              <CardHeader className="p-4">
              <div className="flex items-start gap-3">
                  <RadioGroupItem value={option.value} id={option.value} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {option.icon}
                      <CardTitle className="text-base">{option.label}</CardTitle>
                    </div>
                    <CardDescription>{option.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </RadioGroup>
      
      {/* Range Selection Inputs */}
      {showRangeInputs && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t.table.printDialog.area.range.title}</CardTitle>
            <CardDescription>
              {t.table.printDialog.area.range.desc}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startRow">{t.table.printDialog.area.range.startRow}</Label>
                <Input
                  id="startRow"
                  type="number"
                  min={0}
                  max={tableConfig.rows.length - 1}
                  value={area.startRow ?? 0}
                  onChange={(e) =>
                    onUpdateArea({ startRow: parseInt(e.target.value, 10) })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endRow">{t.table.printDialog.area.range.endRow}</Label>
                <Input
                  id="endRow"
                  type="number"
                  min={area.startRow ?? 0}
                  max={tableConfig.rows.length - 1}
                  value={area.endRow ?? tableConfig.rows.length - 1}
                  onChange={(e) =>
                    onUpdateArea({ endRow: parseInt(e.target.value, 10) })
                  }
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startColumn">{t.table.printDialog.area.range.startCol}</Label>
                <Input
                  id="startColumn"
                  type="number"
                  min={0}
                  max={tableConfig.columns.length - 1}
                  value={area.startColumn ?? 0}
                  onChange={(e) =>
                    onUpdateArea({ startColumn: parseInt(e.target.value, 10) })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endColumn">{t.table.printDialog.area.range.endCol}</Label>
                <Input
                  id="endColumn"
                  type="number"
                  min={area.startColumn ?? 0}
                  max={tableConfig.columns.length - 1}
                  value={area.endColumn ?? tableConfig.columns.length - 1}
                  onChange={(e) =>
                    onUpdateArea({ endColumn: parseInt(e.target.value, 10) })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Column Selector */}
      {showColumnSelector && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t.table.printDialog.area.columns.title}</CardTitle>
            <CardDescription>
              {t.table.printDialog.area.columns.desc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {tableConfig.columns.map((column) => (
                <div key={column.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`col-${column.id}`}
                    checked={area.columnIds?.includes(column.id) ?? false}
                    onCheckedChange={(checked) => {
                      const current = area.columnIds ?? [];
                      if (checked) {
                        onUpdateArea({ columnIds: [...current, column.id] });
                      } else {
                        onUpdateArea({
                          columnIds: current.filter((id) => id !== column.id),
                        });
                      }
                    }}
                  />
                  <Label htmlFor={`col-${column.id}`} className="cursor-pointer">
                    {column.label}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Row Selector */}
      {showRowSelector && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t.table.printDialog.area.rows.title}</CardTitle>
            <CardDescription>
              {t.table.printDialog.area.rows.desc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {tableConfig.rows.map((row, index) => (
                <div key={row.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`row-${row.id}`}
                    checked={area.rowIds?.includes(row.id) ?? false}
                    onCheckedChange={(checked) => {
                      const current = area.rowIds ?? [];
                      if (checked) {
                        onUpdateArea({ rowIds: [...current, row.id] });
                      } else {
                        onUpdateArea({
                          rowIds: current.filter((id) => id !== row.id),
                        });
                      }
                    }}
                  />
                  <Label htmlFor={`row-${row.id}`} className="cursor-pointer">
                    {t.table.printDialog.area.rows.rowLabel.replace('{index}', String(index + 1))}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Header Options */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t.table.printDialog.area.headers.title}</CardTitle>
          <CardDescription>
            {t.table.printDialog.area.headers.desc}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="includeHeaders"
              checked={area.includeHeaders}
              onCheckedChange={(checked) =>
                onUpdateArea({ includeHeaders: Boolean(checked) })
              }
            />
            <Label htmlFor="includeHeaders" className="cursor-pointer">
              {t.table.printDialog.area.headers.includeColumns}
            </Label>
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox
              id="includeHeaderRows"
              checked={area.includeHeaderRows}
              onCheckedChange={(checked) =>
                onUpdateArea({ includeHeaderRows: Boolean(checked) })
              }
              disabled={!tableConfig.headerRows || tableConfig.headerRows.length === 0}
            />
            <Label
              htmlFor="includeHeaderRows"
              className={`cursor-pointer ${
                !tableConfig.headerRows || tableConfig.headerRows.length === 0
                  ? 'text-muted-foreground'
                  : ''
              }`}
            >
              {t.table.printDialog.area.headers.includeRows}
              {(!tableConfig.headerRows || tableConfig.headerRows.length === 0) && (
                <span className="text-xs ml-2">{t.table.printDialog.area.headers.noHeaders}</span>
              )}
            </Label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}